import { createDraftSafeSelector } from "@reduxjs/toolkit";
import { RootState } from "..";
import { trendAlertsAdapter } from "./trendAlertsSlice";

export const {
  selectAll: selectAllTrendAlerts,
  selectById: selectTrendAlertById,
  selectIds: selectTrendAlertIds,
} = trendAlertsAdapter.getSelectors((state: RootState) => state.trendAlerts);

export const selectTrendAlertsByTrackerId = createDraftSafeSelector(
  (state: RootState, trackerId: string): [TrendAlert.Data[], string] => [
    selectAllTrendAlerts(state),
    trackerId,
  ],
  ([alerts, trackerId]) => alerts.filter((a) => a.trackerId === trackerId),
);

export const selectTrendAlertsStatus = (state: RootState) =>
  state.trendAlerts.status;

export const selectTrendAlertsByAuthorId = createDraftSafeSelector(
  (state: RootState, authorId: string): [TrendAlert.Data[], string] => [
    selectAllTrendAlerts(state),
    authorId,
  ],
  ([alerts, authorId]) => alerts.filter((a) => a.authorId === authorId),
);

export const selectTrendAlertsByAuthorAndTracker = createDraftSafeSelector(
  (
    state: RootState,
    authorId: string,
    trackerId: string,
  ): [TrendAlert.Data[], string, string] => [
    selectAllTrendAlerts(state),
    authorId,
    trackerId,
  ],
  ([alerts, authorId, trackerId]) =>
    alerts.filter((a) => a.authorId === authorId && a.trackerId === trackerId),
);

export const selectCurrentVolumeByTrackerId =
  (trackerId: string) => (state: RootState) =>
    state.trendAlerts.currentVolumes[trackerId] ?? null;

export const selectIsLoadingCurrentVolume =
  (trackerId: string) => (state: RootState) =>
    state.trendAlerts.loadingCurrentVolumes[trackerId] ?? false;

export const selectHasDuplicateAlert =
  (
    authorId: string,
    trackerId: string,
    type: TrendAlert.Type,
    params: {
      percentage?: string;
      targetVolume?: number;
      minVolume?: number;
      excludeId?: string;
    },
  ) =>
  (state: RootState): boolean => {
    const alerts = selectTrendAlertsByAuthorAndTracker(
      state,
      authorId,
      trackerId,
    );

    return alerts.some((alert) => {
      if (params.excludeId && alert.id === params.excludeId) return false;

      if (type === "suggestions") {
        return alert.type === type && alert.minVolume === params.minVolume;
      }

      return (
        alert.type === type &&
        (type === "consistent"
          ? alert.percentage === params.percentage
          : alert.targetVolume === params.targetVolume)
      );
    });
  };
